import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import dateFormatType from 'src/utils/dateFormatType';
import selectFormatDate from 'src/utils/selectDateFormat';
import { formatPositiveAmount } from 'src/utils/formatAmout';
import OverflowTipography from 'src/components/OverflowTipography';
import {
  HAIRLINE_BORDER_COLOR,
  FIELD_LABEL_COLOR,
  FIELD_VALUE_COLOR
} from './styleConstants';

const FIELD_LABEL_SX = {
  display: 'block',
  marginBottom: '6px',
  color: FIELD_LABEL_COLOR,
  fontSize: '12px',
  fontWeight: 800,
  letterSpacing: '0.04em',
  textTransform: 'uppercase'
};

const FIELD_VALUE_SX = {
  display: 'block',
  color: FIELD_VALUE_COLOR,
  fontSize: '14px',
  fontWeight: 700
};

const Field = ({ label, value, borderRight, borderBottom, truncate }) => (
  <Box
    sx={{
      minHeight: '78px',
      padding: '16px 20px',
      borderBottom: borderBottom ? `1px solid ${HAIRLINE_BORDER_COLOR}` : 0,
      borderRight: borderRight ? `1px solid ${HAIRLINE_BORDER_COLOR}` : 0
    }}
  >
    <Typography component="span" sx={FIELD_LABEL_SX}>
      {label}
    </Typography>
    {truncate && value ? (
      <OverflowTipography
        component="strong"
        maxWidth="240px"
        fontSize={14}
        sx={FIELD_VALUE_SX}
      >
        {value}
      </OverflowTipography>
    ) : (
      <Typography component="strong" sx={FIELD_VALUE_SX}>
        {value || '—'}
      </Typography>
    )}
  </Box>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  borderRight: PropTypes.bool,
  borderBottom: PropTypes.bool,
  truncate: PropTypes.bool
};

Field.defaultProps = {
  value: null,
  borderRight: false,
  borderBottom: true,
  truncate: false
};

const PaymentFields = ({
  client,
  contractFolioWithoutPrefix,
  date,
  origin,
  associatedInvoiceCount,
  amountIvaIncluded,
  comment,
  hasContract
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
      <Field label={t('TRANSACTION.DETAIL.CLIENT')} value={client} borderRight />
      <Field label={t('TRANSACTION.CONTRACT')} value={contractFolioWithoutPrefix} />
      <Field
        label={t('TRANSACTION.DETAIL.PAYMENT_DATE')}
        value={date ? selectFormatDate(date, dateFormatType.DAY) : null}
        borderRight
      />
      <Field
        label={t('TRANSACTION.ORIGIN_TITLE')}
        value={origin ? t(`TRANSACTION.ORIGIN.${origin}`) : null}
      />
      <Field
        label={t('TRANSACTION.DETAIL.ASSOCIATED_INVOICES')}
        value={associatedInvoiceCount}
        borderRight
      />
      <Field
        label={t('TRANSACTION.DETAIL.DISTRIBUTED_AMOUNT')}
        value={formatPositiveAmount(amountIvaIncluded)}
      />
      <Field
        label={t('TRANSACTION.COMMENT')}
        value={comment}
        truncate
        borderRight
        borderBottom={false}
      />
      <Field
        label={t('TRANSACTION.DETAIL.CONTRACT_STATUS')}
        value={
          hasContract
            ? t('TRANSACTION.DETAIL.CONTRACT_ASSOCIATED')
            : t('TRANSACTION.DETAIL.CONTRACT_PENDING')
        }
        borderBottom={false}
      />
    </Box>
  );
};

PaymentFields.propTypes = {
  client: PropTypes.string,
  contractFolioWithoutPrefix: PropTypes.string,
  date: PropTypes.string,
  origin: PropTypes.string,
  associatedInvoiceCount: PropTypes.number.isRequired,
  amountIvaIncluded: PropTypes.number.isRequired,
  comment: PropTypes.string,
  hasContract: PropTypes.bool.isRequired
};

PaymentFields.defaultProps = {
  client: null,
  contractFolioWithoutPrefix: null,
  date: null,
  origin: null,
  comment: null
};

export default PaymentFields;
